import { useI18n, useT } from "../../../../shared/i18n/i18n";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LayoutTemplate } from "lucide-react";
import { useState } from "react";
import { useTRPC } from "../../../../shared/api/trpc";
import { cn } from "../../../../shared/lib/cn";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Applying a template only adds the fields, statuses and views a board is
// missing, plus the status new work starts in. Nothing existing is removed, so
// it is safe on a board that already has work.
export function ApplyBoardTemplateDialog({
  boardId,
  onApplied,
  onOpenChange,
  open,
}: {
  boardId: string;
  onApplied?: () => Promise<void> | void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) {
  const t = useT();
  const { locale } = useI18n();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const templates = useQuery(trpc.boards.templates.queryOptions({ locale }));
  const applyMutation = useMutation(trpc.boards.applyTemplate.mutationOptions());
  const [templateKey, setTemplateKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function apply() {
    if (!templateKey) return;
    setError(null);
    try {
      await applyMutation.mutateAsync({ boardId, locale, templateKey });
      await queryClient.invalidateQueries({ queryKey: trpc.boards.list.queryKey() });
      await queryClient.invalidateQueries({ queryKey: trpc.workObjectFields.listDefinitions.queryKey() });
      await onApplied?.();
      onOpenChange(false);
    } catch (applyError) {
      setError(applyError instanceof Error ? applyError.message : "Could not apply the template.");
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("board.applyTemplateTitle")}</DialogTitle>
          <DialogDescription>{t("board.applyTemplateHint")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          {(templates.data ?? []).map((template) => (
            <button
              className={cn(
                "flex items-start gap-2.5 rounded-md border px-3 py-2 text-left transition-colors",
                templateKey === template.key ? "border-primary/50 bg-primary/5" : "border-border/60 hover:bg-accent/40",
              )}
              key={template.key}
              onClick={() => setTemplateKey(template.key)}
              type="button"
            >
              <LayoutTemplate className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0">
                <span className="block text-sm font-medium">{template.name}</span>
                <span className="block text-xs text-muted-foreground">{template.description}</span>
              </span>
            </button>
          ))}
        </div>
        {error ? <p className="text-xs text-destructive">{error}</p> : null}
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
            {t("common.cancel")}
          </Button>
          <Button disabled={!templateKey || applyMutation.isPending} onClick={() => void apply()}>
            {t("board.applyTemplate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
